/**
 * Cancellation deadlines of a quotation's suppliers (hotels, restaurants...).
 *
 * A supplier row may carry `deadline: [date_start, date_end]`. Only `date_end` is reliable: past that
 * date the booking can no longer be cancelled and must be paid. `date_start` is informational.
 * The key may be missing or null, in which case nothing is shown.
 */

/** Days left thresholds (inclusive upper bounds for critical/warning, exclusive for safe). */
export const THRESHOLDS = { critical: 21, warning: 60, safe: 180 };

/** Worst-first ordering used to pick the tab's colour/icon. */
export const LEVEL_ORDER = ['critical', 'warning', 'moderate', 'safe', 'passed'];

/** Theme classes/colours per level: solid badge, light pill, text colour, boxicon, hex colour and light tint (Frest palette). */
export const LEVEL_STYLE = {
    critical: { badge: 'badge-danger',    pill: 'badge-light-danger',    text: 'text-danger',  icon: 'bx bxs-error',         color: '#FF5B5C', tint: '#FFDEDE' },
    warning:  { badge: 'badge-warning',   pill: 'badge-light-warning',   text: 'text-warning', icon: 'bx bx-time-five',      color: '#FDAC41', tint: '#FFEED9' },
    moderate: { badge: 'badge-info',      pill: 'badge-light-info',      text: 'text-info',    icon: 'bx bx-calendar',       color: '#00CFDD', tint: '#CCF5F8' },
    safe:     { badge: 'badge-success',   pill: 'badge-light-success',   text: 'text-success', icon: 'bx bx-calendar-check', color: '#39DA8A', tint: '#D2FFE8' },
    passed:   { badge: 'badge-secondary', pill: 'badge-light-secondary', text: 'text-muted',   icon: 'bx bx-lock',           color: '#475F7B', tint: '#E6EAEE' },
};

/** Parse "YYYY-MM-DD" as a local calendar date (avoids the UTC shift of `new Date('YYYY-MM-DD')`). */
export function parseDate(value) {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    const m = String(value).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
}

/** Normalise the server's `deadline` value to {start, end}; null when there is no usable end date. */
export function parseDeadline(deadline) {
    if (!deadline) return null;
    let start = null, end = null;
    if (Array.isArray(deadline)) {
        start = parseDate(deadline[0]);
        end   = parseDate(deadline.length > 1 ? deadline[1] : deadline[0]);
    } else if (typeof deadline === 'object') {
        start = parseDate(deadline.start || deadline.date_start);
        end   = parseDate(deadline.end || deadline.date_end);
    } else {
        end = parseDate(deadline);
    }
    return end ? { start, end } : null;
}

/** Whole calendar days from today (local) to `date`; negative when the date is in the past. */
export function daysUntil(date, now) {
    now = now || new Date();
    const a = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const b = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return Math.round((b - a) / 86400000);
}

export function levelFor(days) {
    if (days < 0) return 'passed';
    if (days <= THRESHOLDS.critical) return 'critical';
    if (days <= THRESHOLDS.warning) return 'warning';
    if (days < THRESHOLDS.safe) return 'moderate';
    return 'safe';
}

/** {start, end, days, level} for a raw `deadline` value, or null when absent/invalid. */
export function describeDeadline(deadline, now) {
    const parsed = parseDeadline(deadline);
    if (!parsed) return null;
    const days = daysUntil(parsed.end, now);
    return { start: parsed.start, end: parsed.end, days, level: levelFor(days) };
}

/** All supplier deadlines of a quotation, soonest first, passed ones last. */
export function collectDeadlines(data, now) {
    const out = [];
    const add = (rows, type) => {
        const list = Array.isArray(rows) ? rows : Object.values(rows || {});
        list.forEach(row => {
            if (!row) return;
            const d = describeDeadline(row.deadline, now);
            if (!d) return;
            out.push({
                type,
                name: row.name || row.hotelName || row.restaurantName || '',
                city: row.hotelCity || row.city || '',
                day:  row.day,
                ...d,
                row,
            });
        });
    };
    add(data && data.hotels, 'hotel');
    add(data && data.restaurants, 'restaurant');
    return out.sort((a, b) => ((a.level === 'passed') - (b.level === 'passed')) || (a.days - b.days));
}

export function countByLevel(list) {
    return (list || []).reduce((acc, d) => { acc[d.level] = (acc[d.level] || 0) + 1; return acc; }, {});
}

/** Most urgent level present in the list (null for an empty list). */
export function worstLevel(list) {
    let worst = null;
    for (const item of (list || [])) {
        if (worst === null || LEVEL_ORDER.indexOf(item.level) < LEVEL_ORDER.indexOf(worst)) worst = item.level;
    }
    return worst;
}
